import React from 'react';
import { Card, Icon } from '@repo/ui';
import { xtndAnimationStyles } from '@repo/tailwind-config';
import { AppealType } from '@repo/types';

interface AppealTypeSelectionProps {
  invoiceNumber: string;
  invoiceStatus: string;
  selectedAppealType: AppealType | null;
  onSelectAppealType: (type: AppealType) => void;
}

/**
 * Component for selecting appeal type (Backlog or Subsequent Appeal)
 * Displays two clickable cards with descriptions and features
 */
export const AppealTypeSelection: React.FC<AppealTypeSelectionProps> = ({
  invoiceNumber,
  invoiceStatus,
  selectedAppealType,
  onSelectAppealType,
}) => {
  return (
    <div className="mt-4 mb-8">
      <style>{xtndAnimationStyles}</style>
      {/* Description */}
      <div
        className="space-y-1 w-full mb-8 text-center opacity-0 animate-fadeInUp"
        style={{ animationDelay: '0.1s' }}
      >
        <h3 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-light">
          Initiate Appeal / Review
        </h3>
        <p className="text-sm  text-xtnd-dark-600 dark:text-xtnd-light-400">
          Invoice #{invoiceNumber} is{' '}
          <span className="font-semibold text-amber-600 dark:text-amber-400">{invoiceStatus}</span>.
          Please select the appeal type.
        </p>
      </div>

      {/* Appeal Type Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Appeal Submitted (Backlog) Card */}
        <Card
          className={`cursor-pointer transition-all duration-200 !border-2 opacity-0 animate-fadeInLeft ${
            selectedAppealType === AppealType.BACKLOG
              ? 'border-xtnd-blue/80 bg-xtnd-light/30 dark:bg-xtnd-dark-700'
              : 'border-gray-200 dark:border-xtnd-dark-600 hover:border-xtnd-blue hover:shadow-md'
          }`}
          style={{ animationDelay: '0.2s' }}
          onClick={() => onSelectAppealType(AppealType.BACKLOG)}
        >
          <div className="p-3 space-y-4">
            {/* Icon */}
            <div className="flex flex-col items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gray-100 dark:bg-xtnd-dark-700 flex items-center justify-center">
                <Icon
                  name="HiClock"
                  size="sm"
                  className={`w-6 h-6 ${
                    selectedAppealType === AppealType.BACKLOG
                      ? 'text-xtnd-blue dark:text-xtnd-blue-300'
                      : 'text-xtnd-dark-600 dark:text-xtnd-light-400'
                  }`}
                />
              </div>
              <div className="flex-1 space-y-2">
                <h4 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Appeal Submitted (Backlog)
                </h4>
                <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                  For appeals submitted before AR Module implementation. No new attorney workflows
                  will be triggered.
                </p>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-2 pt-2 ">
              <div className="flex items-center gap-2">
                <Icon
                  name="HiCheckCircle"
                  className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0"
                />
                <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                  Capture non-appeal discounts
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon
                  name="HiCheckCircle"
                  className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0"
                />
                <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                  Financial reconciliation only
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Subsequent Appeal Card */}
        <Card
          className={`cursor-pointer transition-all duration-200 !border-2 opacity-0 animate-fadeInRight ${
            selectedAppealType === AppealType.SUBSEQUENT
              ? 'border-xtnd-blue/80 bg-xtnd-light/30 dark:bg-xtnd-dark-700'
              : 'border-gray-200 dark:border-xtnd-dark-600 hover:border-xtnd-blue hover:shadow-md'
          }`}
          style={{ animationDelay: '0.2s' }}
          onClick={() => onSelectAppealType(AppealType.SUBSEQUENT)}
        >
          <div className="p-3 space-y-4">
            {/* Icon */}
            <div className="flex flex-col items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gray-100 dark:bg-xtnd-dark-700 flex items-center justify-center">
                <Icon
                  name="HiPlusCircle"
                  className={`w-6 h-6 ${
                    selectedAppealType === AppealType.SUBSEQUENT
                      ? 'text-xtnd-blue dark:text-xtnd-blue-300'
                      : 'text-xtnd-dark-600 dark:text-xtnd-light-400'
                  }`}
                />
              </div>
              <div className="flex-1 space-y-2">
                <h4 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Subsequent Appeal
                </h4>
                <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                  A new follow-on appeal attempt. Full workflow with attorney notifications and
                  responses.
                </p>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-2 pt-2 ">
              <div className="flex items-center gap-2">
                <Icon
                  name="HiCheckCircle"
                  className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0"
                />
                <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                  Full attorney workflow
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon
                  name="HiCheckCircle"
                  className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0"
                />
                <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                  Deadline tracking
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AppealTypeSelection;
